import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Loader2, AlertCircle, RefreshCw } from "lucide-react";

const MAX_RETRIES = 2;

const AuthCallback = () => {
  const navigate = useNavigate();
  const { handleGoogleCallback } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const processCallback = async (sessionId, attempt = 0) => {
    try {
      const user = await handleGoogleCallback(sessionId);
      // Clear hash from URL so back-navigation doesn't re-trigger this flow
      window.history.replaceState(null, '', window.location.pathname);
      navigate('/dashboard', { replace: true, state: { user } });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Authentication failed";
      console.error(`SSO attempt ${attempt + 1} failed:`, msg, err);

      if (attempt < MAX_RETRIES) {
        // Wait 800ms then retry
        await new Promise(r => setTimeout(r, 800));
        return processCallback(sessionId, attempt + 1);
      }

      setError(msg);
    }
  };

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // Extract session_id from URL hash (#session_id=xxx or #?session_id=xxx)
    const hash = window.location.hash;
    const cleaned = hash.replace(/^#\?/, '').replace(/^#/, '');
    const params = new URLSearchParams(cleaned);
    const sessionId = params.get('session_id');

    if (!sessionId) {
      console.error("No session_id found in URL hash:", hash);
      navigate('/login', { replace: true });
      return;
    }

    processCallback(sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetry = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-6 h-6 text-red-400" />
          </div>
          <h2 className="text-white font-semibold text-lg mb-2">Sign-in failed</h2>
          <p className="text-muted-foreground text-sm mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={handleRetry}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
              data-testid="sso-retry-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Try again
            </button>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="px-4 py-2 border border-border text-muted-foreground rounded-lg text-sm hover:bg-muted transition-colors"
              data-testid="sso-back-login-btn"
            >
              Back to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
        <p className="text-white font-medium mb-1">Signing you in…</p>
        <p className="text-muted-foreground text-sm">Please wait</p>
      </div>
    </div>
  );
};

export default AuthCallback;
